<template>
  <section class="bg-[#fcf5ec] py-12 px-4">
    <div class="container mx-auto max-w-[960px] bg-[#EEDCC5] p-6 lg:p-12">
      <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-6">{{ t('titles.register') }}</h2>
      <p class="text-[15px] text-[#333] mb-8">{{ t('descriptions.registerIntro') }}</p>

      <!-- SUCCESS STATE -->
      <div v-if="submitted" class="bg-[#e7d7bf] p-5 mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">✓</div>
          <h3 class="text-[20px] font-black italic uppercase">{{ t('titles.received') }}</h3>
        </div>
        <p class="text-[14px] text-[#166534] mb-4">
          {{ successMessage || t('messages.successRegister') }}
        </p>
        <button type="button" @click="$emit('backToLogin')" class="h-12 bg-black text-white italic uppercase font-extrabold px-8">{{ t('buttons.login') }}</button>
      </div>

      <!-- 1 REGIO -->
      <div v-if="!submitted" class="bg-[#e7d7bf] p-5 mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">1</div>
          <h3 class="text-[20px] font-black italic uppercase">{{ t('titles.step1') }}</h3>
        </div>
        <p class="text-[14px] text-[#666] mb-4">{{ t('descriptions.step1') }}</p>
        <div>
          <input ref="refPostcode" v-model="postcode" type="text" :placeholder="t('placeholders.postcode')" @input="debouncedValidatePostcode" @blur="validatePostcode(true)"
                 class="w-full h-12 bg-white px-3 border" :class="postcodeClass" />
          <div v-if="postcodeMessage" :class="['mt-3 text-white px-3 py-2 text-[14px]', postcodeOk ? 'bg-[#3FA448]' : 'bg-[#b91c1c]']"
               v-html="postcodeMessage">
          </div>
          <div v-if="postcodeMessage && !postcodeOk && regionInactiveMessage" class="mt-2">
            <button type="button" @click="showInterestForm = !showInterestForm"
                    class="text-[14px] underline font-bold text-[#111]">
              {{ showInterestForm ? 'Verberg interesseformulier' : 'Laat je gegevens hier achter' }}
            </button>
          </div>
        </div>
      </div>

      <!-- INTEREST FORM (When region is inactive) -->
      <div v-if="!submitted && showInterestForm" class="bg-[#e7d7bf] p-5 mb-8 border-2 border-black">
        <h3 class="text-[20px] font-black italic uppercase mb-2">Interesseformulier</h3>
        <p class="text-[14px] mb-4">Laat je gegevens achter en dan nemen we contact met je op zodra we actief zijn in jouw regio.</p>
        <div v-if="interestSubmitted" class="bg-[#3FA448] text-white p-4 mb-4">
          {{ t('messages.interestSuccess') }}
        </div>
        <div v-else class="grid grid-cols-1 gap-4">
          <div>
            <input v-model="interestName" type="text" :placeholder="t('contact.namePlaceholder')" class="w-full h-12 bg-white px-3 border border-[#d8d1c6]" />
          </div>
          <div>
            <input v-model="interestEmail" type="email" :placeholder="t('placeholders.email')" class="w-full h-12 bg-white px-3 border border-[#d8d1c6]" />
          </div>
          <div>
            <input v-model="interestOrgName" type="text" :placeholder="interestOrgPlaceholder" class="w-full h-12 bg-white px-3 border border-[#d8d1c6]" />
          </div>
          <div v-if="interestError" class="text-[#b91c1c] text-[14px]">{{ interestError }}</div>
          <button @click="submitInterestForm" :disabled="interestLoading || !interestFormValid"
                  class="h-12 bg-black text-white italic uppercase font-extrabold px-8 disabled:opacity-60">
            {{ interestLoading ? t('messages.loading') : t('buttons.submitRequest') }}
          </button>
        </div>
      </div>

      <!-- 2 TYPE -->
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage" class="bg-[#e7d7bf] p-5 mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">2</div>
          <h3 class="text-[20px] font-black italic uppercase">{{ t('titles.step2') }}</h3>
        </div>
        <p class="text-[14px] text-[#666] mb-4">{{ t('descriptions.step2') }}</p>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <button type="button" @click="role='bedrijf'" :aria-pressed="role==='bedrijf'"
                  class="h-40 border-2" :class="role==='bedrijf' ? 'border-[#F7941E] bg-[#f6efe4]' : 'border-[#d8d1c6] bg-[#ffffff]'">
            <div class="w-full h-full flex items-center justify-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="49.733" height="46.985" viewBox="0 0 49.733 46.985">
                <g id="Group_717" data-name="Group 717" transform="translate(0)">
                  <path id="Path_226" data-name="Path 226" d="M149.9,25.236a18.1,18.1,0,0,0,36.047-3.263" transform="translate(-142.724 3.563)" fill="#fff"/>
                  <path id="Path_227" data-name="Path 227" d="M168.485,44.016a19.827,19.827,0,0,1-19.7-18.033,1.722,1.722,0,1,1,3.429-.31,16.377,16.377,0,0,0,32.619-2.953,1.721,1.721,0,0,1,3.428-.311,19.842,19.842,0,0,1-17.951,21.524q-.913.082-1.821.082" transform="translate(-143.317 2.969)"/>
                  <path id="Path_228" data-name="Path 228" d="M176,7.635h0a19.081,19.081,0,0,1,10.935,15.59l-38.073,3.446a19.1,19.1,0,0,1,7.957-17.305" transform="translate(-143.314 -3.981)" fill="#e24c3b"/>
                  <path id="Path_229" data-name="Path 229" d="M149.449,28.982a1.721,1.721,0,0,1-1.714-1.566,20.939,20.939,0,0,1,8.672-18.857,1.721,1.721,0,1,1,2,2.8,17.5,17.5,0,0,0-7.312,14.022l34.475-3.121a17.47,17.47,0,0,0-9.711-12.478,1.721,1.721,0,0,1,1.459-3.116,20.916,20.916,0,0,1,11.925,16.994,1.722,1.722,0,0,1-1.56,1.868L149.6,28.975c-.053,0-.1.006-.156.006" transform="translate(-143.906 -4.568)"/>
                  <path id="Path_230" data-name="Path 230" d="M192.6,20.606a3.112,3.112,0,0,1-2.8,3.358l-40.085,3.629a3.134,3.134,0,0,1-3.194-4.231,3,3,0,0,1,2.613-1.937l.012,0L189.233,17.8a3.11,3.11,0,0,1,3.371,2.813" transform="translate(-144.6 1.358)" fill="#e24c3b"/>
                  <path id="Path_231" data-name="Path 231" d="M150.027,29.919a4.818,4.818,0,0,1-.44-9.616l40.085-3.628a4.819,4.819,0,1,1,.882,9.6L150.469,29.9a4.313,4.313,0,0,1-.443.02M190.109,20.1a.892.892,0,0,0-.127.006L149.9,23.73a1.365,1.365,0,0,0-1.241,1.482v.006a1.386,1.386,0,0,0,1.5,1.253l40.086-3.629a1.291,1.291,0,0,0,.91-.476,1.307,1.307,0,0,0,.331-1.012,1.388,1.388,0,0,0-1.375-1.259" transform="translate(-145.198 0.765)"/>
                  <path id="Path_232" data-name="Path 232" d="M154.8,18.266l-.73-8.064v0a2.3,2.3,0,0,1,2.082-2.5l3.144-.285,4.144-.375,4.174-.377,3.287-.3a2.142,2.142,0,0,1,2.326,1.938l.014.157.73,8.064" transform="translate(-140.537 -4.652)" fill="#e24c3b"/>
                  <path id="Path_233" data-name="Path 233" d="M155.388,20.579a1.721,1.721,0,0,1-1.711-1.566l-.745-8.228a.707.707,0,0,0-.006-.113,4.03,4.03,0,0,1,3.661-4.093l14.6-1.323a4.026,4.026,0,0,1,4.324,3.371c.006.038.011.076.014.119l.745,8.22a1.721,1.721,0,0,1-3.428.311l-.74-8.147a.571.571,0,0,0-.606-.443L156.9,10.01a.583.583,0,0,0-.53.546L157.1,18.7a1.721,1.721,0,0,1-1.558,1.87c-.053,0-.105.008-.157.008" transform="translate(-141.132 -5.241)"/>
                  <path id="Path_235" data-name="Path 235" d="M164.566,34.006a7.751,7.751,0,0,1-6.709-3.884,1.721,1.721,0,0,1,2.975-1.731,4.3,4.3,0,0,0,3.738,2.169h.382a4.291,4.291,0,0,0,3.626-2.8,1.721,1.721,0,0,1,3.234,1.177,7.752,7.752,0,0,1-6.6,5.056h-.035c-.2.015-.409.023-.612.023" transform="translate(-138.66 6.024)"/>
                </g>
              </svg>
              <span class="text-[18px]">{{ t('roles.company') }}</span>
              <span class="w-3 h-3 rounded-full border-2 ml-2" :class="role==='bedrijf' ? 'bg-[#F7941E] border-[#F7941E]' : 'bg-transparent border-[#8a7f6e]'" aria-hidden="true"></span>


            </div>
          </button>
          <button type="button" @click="role='school'" :aria-pressed="role==='school'"
                  class="h-40 border-2" :class="role==='school' ? 'border-[#F7941E] bg-[#f6efe4]' : 'border-[#d8d1c6] bg-[#ffffff]'">
            <div class="w-full h-full flex items-center justify-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="51.643" height="46.331" viewBox="0 0 51.643 46.331">
                <g id="Group_716" data-name="Group 716" transform="translate(0)">
                  <path id="Path_219" data-name="Path 219" d="M194.806,70.116a19.877,19.877,0,1,1-23.263-15.779A19.877,19.877,0,0,1,194.806,70.116Z" transform="translate(-146.112 -49.127)" fill="#fff"/>
                  <path id="Path_220" data-name="Path 220" d="M175.879,96.068a21.628,21.628,0,1,1,4.073-.389,21.624,21.624,0,0,1-4.073.389m.014-39.76A18.153,18.153,0,1,0,193.718,71.05a18.157,18.157,0,0,0-17.826-14.741" transform="translate(-146.721 -49.737)"/>
                  <path id="Path_221" data-name="Path 221" d="M187.934,54.578s11.089-2.681,12.759,9.569c.7,5.161-.043,9.221-1.72,11.466a1.93,1.93,0,0,1-2.567.485,25.487,25.487,0,0,1-7.1-6.936s-2.5,7.051-8.809,8.957a1.517,1.517,0,0,1-1.879-1.038c-.011-.041-.021-.082-.031-.124-.337-1.648-1.155-4.156-3.065-5.053,0,0-4.788,8.6-14.689,9.946-4.5.612-7.263-2.405-9.307-4.25a1.1,1.1,0,0,1-.088-1.555,1.084,1.084,0,0,1,.446-.3c2.175-.777,4.96-1.917,5.191-5.758,0,0-5.909,1.43-7.71-4.336-.153-.49.078-1.012.589-.971,1.516.127,3.744,1.912,6.688-.781,3.017-1.789,10.563-11.915,18.264-12.96a17.456,17.456,0,0,1,13.026,3.641" transform="translate(-149.316 -50.799)" fill="#f7941e"/>
                  <path id="Path_222" data-name="Path 222" d="M172.335,61.965s-5.064,9.093-15.538,10.52c-2.663.253-3.982-.382-6.247-1.807a1.1,1.1,0,0,0,.34,1.048c2.045,1.845,4.8,4.864,9.308,4.25,9.9-1.349,14.689-9.946,14.689-9.946a3.375,3.375,0,0,1,.436.267,6.566,6.566,0,0,0-2.99-4.334" transform="translate(-148.679 -44.925)"/>
                  <path id="Path_223" data-name="Path 223" d="M189.85,56.964c.614,5.183-.177,9.264-1.888,11.552a2.043,2.043,0,0,1-2.717.514,26.96,26.96,0,0,1-7.512-7.335s-2.643,7.455-9.322,9.471a1.486,1.486,0,0,1-.441.06,11.7,11.7,0,0,1,.8,2.488,1.518,1.518,0,0,0,1.786,1.19,1.217,1.217,0,0,0,.124-.031c6.306-1.908,8.809-8.957,8.809-8.957a25.481,25.481,0,0,0,7.1,6.936,1.932,1.932,0,0,0,2.575-.482c1.676-2.245,2.422-6.3,1.719-11.466a15.475,15.475,0,0,0-1.032-3.934" transform="translate(-139.5 -47.556)"/>
                  <path id="Path_225" data-name="Path 225" d="M171.563,79.69a8.346,8.346,0,0,1-7.274-4.278,1.721,1.721,0,0,1,3.008-1.67,4.879,4.879,0,0,0,4.582,2.5h.029a4.889,4.889,0,0,0,4.2-3.136,1.721,1.721,0,0,1,3.22,1.216A8.341,8.341,0,0,1,172.2,79.67h-.041c-.2.014-.4.021-.6.021" transform="translate(-141.543 -39.648)"/>
                </g>
              </svg>
              <span class="text-[18px]">{{ t('roles.school') }}</span>
              <span class="w-3 h-3 rounded-full border-2 ml-2" :class="role==='school' ? 'bg-[#F7941E] border-[#F7941E]' : 'bg-transparent border-[#8a7f6e]'" aria-hidden="true"></span>
            </div>
          </button>
        </div>
      </div>

      <!-- 3 MIJN GEGEVENS -->
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage" class="bg-[#e7d7bf] p-5 mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">3</div>
          <h3 class="text-[20px] font-black italic uppercase">{{ t('titles.step3') }}</h3>
        </div>
        <p class="text-[14px] text-[#666] mb-4">{{ t('descriptions.step3') }}</p>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <input ref="refFirstName" v-model="firstName" type="text" :placeholder="t('placeholders.firstName')"
                 @blur="firstNameTouched=true" @input="firstNameTouched=true"
                 :class="['h-12 px-3 border bg-white', (firstNameTouched && !firstName) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
          <input ref="refLastName" v-model="lastName" type="text" :placeholder="t('placeholders.lastName')"
                 @blur="lastNameTouched=true" @input="lastNameTouched=true"
                 :class="['h-12 px-3 border bg-white', (lastNameTouched && !lastName) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
          <div>
            <input ref="refEmail" v-model="email" type="email" :placeholder="t('placeholders.email')"
                   @input="emailTouched=true" @blur="emailTouched=true"
                   :class="['h-12 px-3 border bg-white w-full', (emailTouched && !isValidEmail(email)) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
            <p v-if="emailTouched && !isValidEmail(email)" class="mt-1 text-[13px] text-[#b91c1c]">{{ t('messages.emailInvalid') }}</p>
          </div>
          <div>
              <input ref="refEmail2" v-model="email2" type="email" :placeholder="t('placeholders.emailConfirm')"
                   @input="email2Touched=true" @blur="email2Touched=true"
                   :class="['h-12 px-3 border bg-white w-full', (email2Touched && (!isValidEmail(email2) || (email && email2 && email!==email2))) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
            <p v-if="email2Touched && (!isValidEmail(email2))" class="mt-1 text-[13px] text-[#b91c1c]">{{ t('messages.emailInvalid') }}</p>
            <p v-else-if="email2Touched && email && email2 && email!==email2" class="mt-1 text-[13px] text-[#b91c1c]">{{ t('messages.emailsNoMatch') }}</p>
          </div>
        </div>
      </div>

      <!-- 4 ORGANISATIE GEGEVENS -->
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage" class="bg-[#e7d7bf] p-5 mb-8">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">4</div>
          <h3 class="text-[20px] font-black italic uppercase">{{ t('titles.step4') }}</h3>
        </div>
        <p class="text-[14px] text-[#666] mb-4">{{ t('descriptions.step4') }}</p>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <input ref="refOrgName" v-model="orgName" type="text" :placeholder="orgNamePlaceholder" class="h-12 px-3 border border-[#d8d1c6] bg-white" />
          <input ref="refFunctionName" v-model="functionName" type="text" :placeholder="t('placeholders.functionName')" class="h-12 px-3 border border-[#d8d1c6] bg-white" />
          <input ref="refStreet" v-model="street" type="text" :placeholder="t('placeholders.street')" class="h-12 px-3 border border-[#d8d1c6] bg-white" />
          <input ref="refHouseNr" v-model="houseNr" type="text" :placeholder="t('placeholders.houseNr')" class="h-12 px-3 border border-[#d8d1c6] bg-white" />
          <input ref="refOrgPostcode" v-model="orgPostcode" type="text" :placeholder="t('placeholders.orgPostcode')"
                 readonly aria-readonly="true"
                 :class="['h-12 px-3 border bg-white lg:col-span-1', (orgPostcodeTouched && !/^\d{4}[A-Z]{2}$/.test((orgPostcode||'').toUpperCase().replace(/\s+/g,''))) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
          <MmLocationAutocomplete
            ref="refCity"
            v-model="cityLocationData"
            :placeholder="t('placeholders.city')"
            :initialText="city"
            inputClass="h-12 px-3 border border-[#d8d1c6] bg-white w-full"
            wrapperClass="lg:col-span-1"
            :required="true"
            @select="onCitySelect"
            @clear="onCityClear"
          />
          <input ref="refWebsite" v-model="website" type="url" :placeholder="t('placeholders.website')" class="h-12 px-3 border border-[#d8d1c6] bg-white lg:col-span-1" />
          <input ref="refPhone" v-model="phone" type="tel" :placeholder="t('placeholders.phone') + ' *'"
                 @blur="phoneTouched=true" @input="phoneTouched=true"
                 :class="['h-12 px-3 border bg-white lg:col-span-1', (phoneTouched && !phone) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']" />
        </div>
      </div>

      <!-- CONSENT + TURNSTILE -->
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage" class="mb-6">
        <label class="flex items-start gap-3 text-[14px]">
          <input ref="refConsent" type="checkbox" v-model="consent" class="mt-1 w-4 h-4" />
          <span v-html="t('consent.html')"></span>
        </label>
      </div>
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage" class="max-w-[520px] mb-8">
        <div v-if="turnstileEnabled" class="border border-[#e5dfd3] rounded">
          <div ref="turnstile" class="mm-turnstile" />
          <p v-if="turnstileError" class="mt-2 text-[13px] text-[#b91c1c] px-3 pb-3">{{ turnstileError }}</p>
        </div>
        <div v-else class="flex items-center justify-between border border-[#e5dfd3] rounded bg-white p-3">
          <div class="flex items-center gap-2">
            <div class="w-5 h-5 grid place-items-center bg-[#55b36a] text-white text-[12px] font-bold rounded-sm">✓</div>
            <span class="text-[15px] text-[#333]">{{ t('messages.verificationDisabled') }}</span>
          </div>
          <span class="text-[11px] text-[#777]">TURNSTILE</span>
        </div>
      </div>

      <!-- SUBMIT ROW -->
      <div v-if="!submitted && !showInterestForm && !regionInactiveMessage">
        <div v-if="errorMessage" class="mb-4 text-[#b91c1c]">{{ errorMessage }}</div>
        <div v-if="successMessage" class="mb-4 text-[#14532d]">{{ successMessage }}</div>
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
          <button @click="submitForm" :disabled="isLoading || !formValid"
                  class="h-12 bg-black text-white italic uppercase font-extrabold px-8 disabled:opacity-60">{{ t('buttons.submitRequest') }}</button>
          <button type="button" @click="$emit('backToLogin')" class="text-[15px] underline italic">{{ t('buttons.backToLogin') }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import Turnstile from '../lib/turnstile.js'
import MmLocationAutocomplete from './MmLocationAutocomplete.vue'
import { t as i18nT } from '../i18n'
export default {
  name: 'MmRegister',
  components: { MmLocationAutocomplete },
  emits: ['backToLogin'],
  data(){
    return {
      // Expose i18n translate function directly to template context to avoid proxy/method issues
      t: i18nT,
      postcode: '',
      postcodeOk: false,
      postcodeMessage: '',
      role: 'bedrijf',
      firstName: '',
      lastName: '',
      email: '',
      email2: '',
      orgName: '',
      functionName: '',
      street: '',
      houseNr: '',
      orgPostcode: '',
      city: '',
      cityLocationData: null,
      website: '',
      phone: '',
      consent: false,
      // autofill & touched flags
      autoFillOrgPostcode: true,
      postcodeTimeout: null,
      firstNameTouched: false,
      lastNameTouched: false,
      emailTouched: false,
      email2Touched: false,
      orgPostcodeTouched: false,
      phoneTouched: false,
      // feedback
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      submitted: false, // hide form after success
      // Turnstile
      turnstileEnabled: !!(Turnstile && Turnstile.isEnabled && Turnstile.isEnabled()),
      turnstileToken: '',
      turnstileWidgetId: null,
      turnstileError: '',
      // Interest form (for inactive regions)
      showInterestForm: false,
      interestName: '',
      interestEmail: '',
      interestRole: 'bedrijf', // Altijd bedrijf; scholen kunnen zich niet aanmelden via het interesseformulier
      interestOrgName: '',
      interestLoading: false,
      interestError: '',
      interestSubmitted: false,
      // Backend configured regions (postcodes/prefixes)
      regions: (window.materiaalAjax && Array.isArray(window.materiaalAjax.regions)) ? window.materiaalAjax.regions : [],
      // Explicit flag from backend whether any regions are configured at all
      regionsConfigured: !!(window.materiaalAjax && window.materiaalAjax.regionsConfigured)
    }
  },
  mounted(){
    if (this.turnstileEnabled) this.initTurnstile();
    this.api = new WpApiClient();
  },
  watch: {
    role() {
      // Re-validate postcode when role changes to update messages and interest form visibility
      if (this.postcode) {
        this.validatePostcode(true);
      }
    }
  },
  computed: {
    formValid(){
      const emailOk = this.isValidEmail(this.email);
      const email2Ok = this.isValidEmail(this.email2);
      const emailsMatch = emailOk && email2Ok && (this.email === this.email2);
      const addressOk = !!(this.street && this.houseNr && this.orgPostcode && (this.city || this.cityLocationData));
      const phoneOk = !!this.phone;
      const orgOk = !!(this.orgName && this.functionName);
      const consentOk = !!this.consent;
      const orgPostcodeFormatOk = /^\d{4}[A-Z]{2}$/.test((this.orgPostcode||'').toUpperCase().replace(/\s+/g,''));
      const turnstileOk = (!this.turnstileEnabled) || (!!this.turnstileToken);
      const isPostcodeTyping = /^\d{1,4}$/.test((this.postcode||'').toUpperCase().replace(/\s+/g,''));
      const isPostcodeFormatOk = /^\d{4}[A-Z]{2}$/.test((this.postcode||'').toUpperCase().replace(/\s+/g,''));
      const postcodeActuallyOk = (this.postcodeOk || (this.role === 'bedrijf' && isPostcodeFormatOk)) && !isPostcodeTyping;
      return !!(postcodeActuallyOk && this.firstName && this.lastName && emailOk && email2Ok && emailsMatch && orgOk && addressOk && orgPostcodeFormatOk && phoneOk && consentOk && turnstileOk);
    },
    orgNamePlaceholder(){
      return this.role === 'school' ? this.t('placeholders.orgNameSchool') : this.t('placeholders.orgNameCompany');
    },
    postcodeClass() {
      const raw = (this.postcode || '').toUpperCase().replace(/\s+/g,'')
      // Toon alleen rood als het geen cijfer-reeks is die nog aangevuld kan worden, 
      // OF als de postcodeMessage al gevuld is (wat gebeurt bij blur/submit/fout)
      const isTypingDigits = /^\d{1,4}$/.test(raw)
      const showError = raw && !this.postcodeOk && (this.role !== 'bedrijf' || !/^\d{4}[A-Z]{2}$/.test(raw)) && (!isTypingDigits || (this.postcodeMessage && !this.postcodeOk))
      return showError ? 'border-[#b91c1c]' : 'border-[#d8d1c6]'
    },
    interestOrgPlaceholder(){
      return this.interestRole === 'school' ? this.t('placeholders.orgNameSchool') : this.t('placeholders.orgNameCompany');
    },
    interestFormValid(){
      return this.interestName && this.isValidEmail(this.interestEmail) && this.interestRole && this.interestOrgName;
    },
    regionInactiveMessage(){
      return this.postcodeMessage === this.t('messages.regionInactive');
    }
  },
  methods:{
    t(key){ return i18nT(key) },
    debouncedValidatePostcode() {
      if (this.postcodeTimeout) clearTimeout(this.postcodeTimeout)
      this.postcodeTimeout = setTimeout(() => {
        this.validatePostcode()
      }, 500)
    },
    validatePostcode(force = false){
      if (this.postcodeTimeout) clearTimeout(this.postcodeTimeout)
      const raw = (this.postcode||'').trim();
      if (!raw) { this.postcodeOk=false; this.postcodeMessage=''; this.showInterestForm = false; return; }
      const norm = raw.toUpperCase().replace(/\s+/g,'');
      const basicFormat = /^\d{4}[A-Z]{2}$/.test(norm);
      let ok = false;
      const hasRegions = (this.regionsConfigured === true) || (Array.isArray(this.regions) && this.regions.length > 0);
      if (hasRegions) {
        // Validate region: prefix-match (token "602" matches "6021AB"), exact match, or wildcard
        ok = basicFormat && this.regions.some(t => {
           const tNorm = String(t).toUpperCase().replace(/\s+/g,'');
           if (!tNorm) return false;
           if (tNorm.endsWith('*')) return norm.startsWith(tNorm.slice(0, -1));
           return norm.startsWith(tNorm);
        });
      } else {
        // No regions configured: do not claim active; treat as not active (but keep format feedback)
        ok = false;
      }
      this.postcodeOk = ok;
      if (ok || (this.role === 'bedrijf' && basicFormat)) this.showInterestForm = false;
      // Auto-fill organization postcode if user hasn't typed it manually yet
      if (basicFormat && this.autoFillOrgPostcode) {
        this.orgPostcode = norm;
      }
      if (ok) {
        this.postcodeMessage = this.t('messages.postcodeActive');
      } else {
        if (!basicFormat) {
          // If 4 digits are entered but not yet letters, don't immediately show "Invalid" if user is still typing?
          // But the requirement is to validate that letters are added.
          if (!force && /^\d{0,4}$/.test(norm)) {
             this.postcodeMessage = ''; // Let them type
             this.postcodeOk = false;
             return;
          }
          this.postcodeMessage = this.t('messages.postcodeInvalid');
        } else if (this.role === 'bedrijf') {
          this.postcodeMessage = this.t('messages.postcodeActiveCompany');
        } else if (!hasRegions) {
          this.postcodeMessage = this.t('messages.regionsNotConfigured');
        } else {
          this.postcodeMessage = this.t('messages.regionInactive');
        }
      }
    },
    async initTurnstile(){
      const siteKey = (window.materiaalAjax && window.materiaalAjax.turnstile && window.materiaalAjax.turnstile.siteKey) || '';
      const appearance = (window.materiaalAjax && window.materiaalAjax.turnstile && window.materiaalAjax.turnstile.appearance) || 'always';
      const theme = (window.materiaalAjax && window.materiaalAjax.turnstile && window.materiaalAjax.turnstile.theme) || 'auto';
      this.turnstileError='';
      if (!siteKey) { this.turnstileEnabled=false; return; }
      const waitForTurnstile = () => new Promise(resolve => {
        if (window.turnstile && typeof window.turnstile.render === 'function') return resolve();
        const iv = setInterval(() => {
          if (window.turnstile && typeof window.turnstile.render === 'function') { clearInterval(iv); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(iv); resolve(); }, 5000);
      });
      await waitForTurnstile();
      if (!(window.turnstile && this.$refs.turnstile)) return;
      try{
        this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
          sitekey: siteKey,
          theme,
          appearance,
          callback: (token) => { this.turnstileToken = token; this.turnstileError=''; },
          'error-callback': () => { this.turnstileToken=''; this.turnstileError=this.t('messages.turnstileError'); },
          'expired-callback': () => { this.turnstileToken=''; this.turnstileError=this.t('messages.turnstileExpired'); }
        });
      }catch(e){ this.turnstileError = this.t('messages.turnstileLoadFailed'); }
    },
    resetTurnstile(){
      try{ if(window.turnstile && this.turnstileWidgetId){ window.turnstile.reset(this.turnstileWidgetId); } this.turnstileToken=''; }catch(_){ }
    },
    isValidEmail(str){
      const s = String(str||'').trim();
      // Simple yet robust email regex
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
    },
    onOrgPostcodeInput(){
      this.orgPostcodeTouched = true;
      this.autoFillOrgPostcode = false;
    },
    handlePostcodeMessageClick(e) {
      if (e.target && e.target.classList.contains('interest-link')) {
        e.preventDefault();
        this.showInterestForm = !this.showInterestForm;
      }
    },
    async submitInterestForm() {
      if (!this.interestFormValid) return;
      this.interestLoading = true;
      this.interestError = '';
      try {
        const res = await this.api.postAjax('mm_send_interest_mail', {
          name: this.interestName,
          email: this.interestEmail,
          role: this.interestRole,
          orgName: this.interestOrgName,
          postcode: this.postcode
        });

        if (res && res.ok) {
          this.interestSubmitted = true;
        } else {
          this.interestError = (res && res.error) || this.t('messages.submitFailed');
        }
      } catch (e) {
        this.interestError = this.t('messages.genericError');
      } finally {
        this.interestLoading = false;
      }
    },
    onCitySelect(item) {
      this.cityLocationData = item
      this.city = item?.display_name || item?.place_name || ''
    },
    onCityClear() {
      this.cityLocationData = null
      this.city = ''
    },
    scrollToRef(refName){
      const el = this.$refs[refName];
      if (refName === 'refPostcode') this.validatePostcode(true);
      if (el && typeof el.scrollIntoView === 'function') {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_) { el.scrollIntoView(); }
      }
      if (el && typeof el.focus === 'function') {
        try { el.focus({ preventScroll: true }); } catch(_) { try { el.focus(); } catch(__) {} }
      }
    },
    findFirstInvalid(){
      if (!this.postcodeOk) return 'refPostcode';
      if (!this.firstName) { this.firstNameTouched = true; return 'refFirstName'; }
      if (!this.lastName) { this.lastNameTouched = true; return 'refLastName'; }
      if (!this.email) { this.emailTouched = true; return 'refEmail'; }
      if (!this.email2) { this.email2Touched = true; return 'refEmail2'; }
      if (!this.isValidEmail(this.email)) { this.emailTouched = true; return 'refEmail'; }
      if (!this.isValidEmail(this.email2)) { this.email2Touched = true; return 'refEmail2'; }
      if (this.email !== this.email2) { this.email2Touched = true; return 'refEmail2'; }
      if (!this.orgName) return 'refOrgName';
      if (!this.functionName) return 'refFunctionName';
      if (!this.street) return 'refStreet';
      if (!this.houseNr) return 'refHouseNr';
      const orgPcOk = /^\d{4}[A-Z]{0,2}$/.test((this.orgPostcode||'').toUpperCase().replace(/\s+/g,''));
      if (!this.orgPostcode || !orgPcOk) { this.orgPostcodeTouched = true; return 'refOrgPostcode'; }
      if (!this.city && !this.cityLocationData) return 'refCity';
      if (!this.website) return 'refWebsite';
      if (!this.phone) { this.phoneTouched = true; return 'refPhone'; }
      if (!this.consent) return 'refConsent';
      if (this.turnstileEnabled && !this.turnstileToken) return 'turnstile';
      return '';
    },
    validate(){
      if(!this.postcodeOk) return this.t('validation.step1Invalid');
      if(!this.firstName || !this.lastName) return this.t('validation.nameMissing');
      if(!this.email || !this.email2) return this.t('validation.emailMissing');
      if(!this.isValidEmail(this.email) || !this.isValidEmail(this.email2)) return this.t('validation.emailInvalid');
      if(this.email !== this.email2) return this.t('validation.emailsNoMatch');
      if(!this.orgName || !this.functionName) return this.t('validation.orgMissing');
      if(!this.street || !this.houseNr || !this.orgPostcode || (!this.city && !this.cityLocationData)) return this.t('validation.addressMissing');
      if(!this.website) return this.t('validation.websiteMissing');
      if(!this.phone) return this.t('validation.phoneMissing');
      if(!this.consent) return this.t('validation.consentMissing');
      if(this.turnstileEnabled && !this.turnstileToken) return this.t('validation.robotCheckMissing');
      return '';
    },
    async submitForm(){
      this.successMessage='';
      // Client-side: no bottom error message; scroll to first invalid input
      if (!this.formValid) {
        const ref = this.findFirstInvalid();
        if (ref) this.scrollToRef(ref);
        return;
      }
      this.isLoading = true; this.errorMessage='';
      try{
        let result = await this.api.registerRequest({
          role: this.role,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          orgName: this.orgName,
          functionName: this.functionName,
          street: this.street,
          houseNr: this.houseNr,
          postcode: this.orgPostcode,
          city: this.cityLocationData?.display_name || this.city,
          city_location_data: this.cityLocationData ? JSON.stringify(this.cityLocationData) : undefined,
          website: this.website,
          phone: this.phone,
          cf_turnstile_token: (this.turnstileEnabled && this.turnstileToken) ? this.turnstileToken : undefined,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined,
        });

        // Automatische retry bij nonce error
        if (result && !result.ok && result.error && result.error.includes('nonce')) {
          const newNonce = await this.api.refreshNonce(false);
          if (newNonce) {
            result = await this.api.registerRequest({
              role: this.role,
              firstName: this.firstName,
              lastName: this.lastName,
              email: this.email,
              orgName: this.orgName,
              functionName: this.functionName,
              street: this.street,
              houseNr: this.houseNr,
              postcode: this.orgPostcode,
              city: this.cityLocationData?.display_name || this.city,
              city_location_data: this.cityLocationData ? JSON.stringify(this.cityLocationData) : undefined,
              website: this.website,
              phone: this.phone,
              cf_turnstile_token: (this.turnstileEnabled && this.turnstileToken) ? this.turnstileToken : undefined,
              nonce: newNonce,
            });
          }
        }

        if (result && result.ok){
          this.successMessage = this.t('messages.successRegister');
          this.submitted = true;
        } else {
          this.errorMessage = (result && result.error) || this.t('messages.submitFailed');
          this.resetTurnstile();
        }
      } catch(e){ this.errorMessage = e?.message || this.t('messages.genericError'); this.resetTurnstile(); }
      this.isLoading = false;
    }
  }
}
</script>
